import React, { useState, useEffect } from 'react';
import { Box, Typography, Link, Skeleton, Divider } from '@mui/material';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import { getGameNews } from '../../services/api';
import { useInView } from 'react-intersection-observer';

interface NewsItem {
  title: string;
  link: string;
  source: string;
  published: string;
}

interface GameNewsSectionProps {
  gameId: number;
}

const GameNewsSection: React.FC<GameNewsSectionProps> = ({ gameId }) => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: true
  });

  useEffect(() => {
    const fetchNews = async () => {
      if (!inView) return;
      
      setLoading(true);
      try {
        const response = await getGameNews(gameId);
        setNews(response.news);
      } catch (err) {
        setError('ニュースの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [gameId, inView]);

  if (error) {
    return null;
  }

  return (
    <Box sx={{ mt: { xs: 3, sm: 3 } }} ref={ref}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 1.5 
      }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>関連ニュース</Typography>
      </Box>

      <Box sx={{ 
        bgcolor: 'rgba(255, 255, 255, 0.03)',
        borderRadius: 2,
        border: '1px solid rgba(255, 255, 255, 0.08)',
        overflow: 'hidden',
        minHeight: '100px'
      }}>
        {loading ? (
          <Box sx={{ p: 2 }}>
            {[...Array(3)].map((_, i) => (
              <Box key={i} sx={{ mb: 2 }}>
                <Skeleton width="100%" height={24} />
                <Skeleton width="30%" height={20} />
              </Box>
            ))}
          </Box>
        ) : news.length > 0 ? (
          <Box>
            {news.map((item, index) => (
              <React.Fragment key={index}>
                <Box sx={{ 
                  p: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  }
                }}>
                  <Link
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'inherit',
                      textDecoration: 'none',
                      display: 'block',
                      '&:hover': {
                        textDecoration: 'none',
                      }
                    }}
                  >
                    <Typography 
                      className="news-title"
                      variant="body2"
                      sx={{ 
                        fontSize: '0.75rem',
                        fontWeight: 400,
                        lineHeight: 1.35,
                        color: 'rgba(255, 255, 255, 0.87)',
                        transition: 'color 0.2s ease'
                      }}
                    >
                      {item.title.replace(/ - [^-]+$/, '')}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: 1,
                      mt: 0.75
                    }}>
                      {item.source && (
                        <Typography 
                          variant="body2"
                          sx={{ 
                            fontSize: '0.675rem',
                            color: 'rgba(255, 255, 255, 0.5)',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                          {item.source}
                        </Typography>
                      )}
                      <Typography 
                        variant="body2"
                        sx={{ 
                          fontSize: '0.675rem',
                          color: 'rgba(255, 255, 255, 0.5)',
                          display: 'flex',
                          alignItems: 'center',
                          '&::before': {
                            content: '""',
                            display: 'inline-block',
                            width: '3px',
                            height: '3px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            marginRight: '8px'
                          }
                        }}>
                        {new Date(item.published).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  </Link>
                </Box>
                {index < news.length - 1 && (
                  <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.05)' }} />
                )}
              </React.Fragment>
            ))}
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            <Typography sx={{ 
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '0.75rem'
            }}>
              関連ニュースはありません
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default GameNewsSection; 